/**
 * PhraseHints - Recipe-specific phrase hints and biasing for STT
 * 
 * Provides domain-specific vocabulary to improve speech recognition
 * accuracy for cooking-related terms, ingredients, and techniques.
 */

export class PhraseHints {
    constructor() {
        this.recipeContext = null;
        this.customHints = [];
        this.baseHints = this.getBaseCookingHints();
    }

    /**
     * Set recipe context for phrase hints
     * @param {Object} recipe - Recipe object with ingredients, steps, etc.
     */
    setRecipeContext(recipe) {
        this.recipeContext = recipe;
        this.updateRecipeHints();
    }

    /**
     * Get base cooking-related phrase hints
     * @returns {Array} Base phrase hints
     */
    getBaseCookingHints() {
        return [
            // Cooking techniques
            'sauté', 'braise', 'roast', 'bake', 'grill', 'fry', 'steam', 'boil', 'simmer',
            'whisk', 'stir', 'fold', 'knead', 'mix', 'blend', 'chop', 'dice', 'slice',
            'mince', 'grate', 'peel', 'julienne', 'brunoise', 'chiffonade',
            
            // Common ingredients
            'onion', 'garlic', 'tomato', 'potato', 'carrot', 'celery', 'bell pepper',
            'olive oil', 'butter', 'salt', 'pepper', 'flour', 'sugar', 'eggs', 'milk',
            'cheese', 'parmesan', 'cheddar', 'mozzarella', 'cream', 'yogurt',
            'chicken', 'beef', 'pork', 'fish', 'salmon', 'shrimp', 'bacon',
            'herbs', 'basil', 'oregano', 'thyme', 'rosemary', 'parsley', 'cilantro',
            'spices', 'cumin', 'paprika', 'cinnamon', 'nutmeg', 'ginger', 'turmeric',
            
            // Measurements and quantities
            'cup', 'tablespoon', 'teaspoon', 'pound', 'ounce', 'gram', 'kilogram',
            'half', 'quarter', 'whole', 'pinch', 'dash', 'handful', 'bunch',
            
            // Cooking states and temperatures
            'hot', 'cold', 'warm', 'room temperature', 'boiling', 'simmering',
            'medium heat', 'high heat', 'low heat', 'preheated', 'golden brown',
            'tender', 'crispy', 'soft', 'firm', 'al dente',
            
            // Kitchen equipment
            'pan', 'pot', 'skillet', 'oven', 'stovetop', 'microwave', 'blender',
            'food processor', 'mixer', 'whisk', 'spatula', 'tongs', 'knife',
            'cutting board', 'measuring cup', 'measuring spoon',
            
            // Time references
            'minutes', 'seconds', 'hours', 'until', 'for', 'about', 'approximately',
            'quickly', 'slowly', 'gradually', 'immediately', 'meanwhile',
            
            // Common cooking phrases
            'add to', 'remove from', 'set aside', 'bring to', 'reduce heat',
            'increase heat', 'turn off', 'let rest', 'serve immediately',
            'garnish with', 'season with', 'taste and adjust'
        ];
    }

    /**
     * Update recipe-specific hints based on current recipe
     */
    updateRecipeHints() {
        if (!this.recipeContext) return;

        const recipeHints = [];

        // Extract ingredients
        if (this.recipeContext.ingredients) {
            this.recipeContext.ingredients.forEach(ingredient => {
                if (typeof ingredient === 'string') {
                    // Parse ingredient string for key terms
                    const words = ingredient.toLowerCase()
                        .replace(/[^\w\s]/g, ' ')
                        .split(/\s+/)
                        .filter(word => word.length > 2);
                    
                    recipeHints.push(...words);
                } else if (ingredient.name) {
                    recipeHints.push(ingredient.name.toLowerCase());
                }
            });
        }

        // Extract cooking methods from steps
        if (this.recipeContext.steps) {
            this.recipeContext.steps.forEach(step => {
                if (step.instructions) {
                    const words = step.instructions.toLowerCase()
                        .replace(/[^\w\s]/g, ' ')
                        .split(/\s+/)
                        .filter(word => word.length > 3);
                    
                    recipeHints.push(...words);
                }
            });
        }

        // Extract recipe title words
        if (this.recipeContext.title) {
            const titleWords = this.recipeContext.title.toLowerCase()
                .replace(/[^\w\s]/g, ' ')
                .split(/\s+/)
                .filter(word => word.length > 2);
            
            recipeHints.push(...titleWords);
        }

        // Remove duplicates and filter out common words
        const commonWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
        this.recipeHints = [...new Set(recipeHints)]
            .filter(word => !commonWords.includes(word))
            .slice(0, 50); // Limit to 50 recipe-specific hints
    }

    /**
     * Add custom phrase hints
     * @param {Array} hints - Custom phrase hints to add
     */
    addCustomHints(hints) {
        this.customHints = [...new Set([...this.customHints, ...hints])];
    }

    /**
     * Clear custom hints
     */
    clearCustomHints() {
        this.customHints = [];
    }

    /**
     * Get all phrase hints for STT
     * @returns {Array} Combined phrase hints
     */
    getAllHints() {
        const allHints = [
            ...this.baseHints,
            ...(this.recipeHints || []),
            ...this.customHints
        ];

        // Remove duplicates and return
        return [...new Set(allHints)];
    }

    /**
     * Get phrase hints formatted for different STT engines
     * @param {string} engineType - Type of STT engine
     * @returns {Object} Formatted hints for the engine
     */
    getFormattedHints(engineType) {
        const hints = this.getAllHints();

        switch (engineType) {
            case 'streaming':
                // For server-side STT, send as context
                return {
                    context: hints,
                    boost: this.getBoostedTerms()
                };

            case 'browser':
                // For Web Speech API, use speechRecognition.grammars
                return {
                    grammars: this.createGrammarList(hints)
                };

            case 'vad':
                // For VAD engine, pass through to underlying engine
                return {
                    hints: hints,
                    context: this.recipeContext
                };

            default:
                return {
                    hints: hints
                };
        }
    }

    /**
     * Get boosted terms (higher confidence for important words)
     * @returns {Object} Boosted terms with confidence scores
     */
    getBoostedTerms() {
        const boosted = {};
        
        // Boost recipe title words
        if (this.recipeContext?.title) {
            const titleWords = this.recipeContext.title.toLowerCase().split(/\s+/);
            titleWords.forEach(word => {
                if (word.length > 2) {
                    boosted[word] = 1.5; // 50% boost
                }
            });
        }

        // Boost main ingredients
        if (this.recipeContext?.ingredients) {
            this.recipeContext.ingredients.slice(0, 5).forEach(ingredient => {
                const name = typeof ingredient === 'string' ? ingredient : ingredient.name;
                if (name) {
                    const words = name.toLowerCase().split(/\s+/);
                    words.forEach(word => {
                        if (word.length > 2) {
                            boosted[word] = 1.3; // 30% boost
                        }
                    });
                }
            });
        }

        return boosted;
    }

    /**
     * Create grammar list for Web Speech API
     * @param {Array} hints - Phrase hints
     * @returns {string} Grammar list in JSGF format
     */
    createGrammarList(hints) {
        const grammar = `#JSGF V1.0;
grammar cooking;
public <cooking> = ${hints.slice(0, 100).join(' | ')};`;
        
        return grammar;
    }

    /**
     * Get context summary for debugging
     * @returns {Object} Context summary
     */
    getContextSummary() {
        return {
            hasRecipe: !!this.recipeContext,
            recipeTitle: this.recipeContext?.title || null,
            baseHintsCount: this.baseHints.length,
            recipeHintsCount: this.recipeHints?.length || 0,
            customHintsCount: this.customHints.length,
            totalHints: this.getAllHints().length,
            boostedTerms: Object.keys(this.getBoostedTerms()).length
        };
    }

    /**
     * Clear all context
     */
    clearContext() {
        this.recipeContext = null;
        this.recipeHints = null;
        this.customHints = [];
    }
}

